import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import functions from '../../../util/functions';
import { Skeleton, SkeletonCircle } from '../../../util/skeleton/Skeleton';

const themeColor = "#4F46E5";


/* ---------- helpers ---------- */
const initialsOf = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

const StatusBadge = ({ status }) => {
  const isActive = String(status).toLowerCase() === 'active';
  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '700',
      textTransform: 'uppercase',
      backgroundColor: isActive ? '#DCFCE7' : '#FEF3C7',
      color: isActive ? '#166534' : '#92400E',
      display: 'inline-flex',
      alignItems: 'center'
    }}>
      <span style={{ 
          width: '6px', height: '6px', borderRadius: '50%', 
          backgroundColor: isActive ? '#166534' : '#92400E', marginRight: '6px' 
      }} />
      {status}
    </span>
  );
};

const RolePill = ({ role }) => (
  <span className="border" style={{
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#475569',
    backgroundColor: '#F8FAFC',
    textTransform: 'uppercase'
  }}>
    <i className="fa fa-user-shield mr-1" style={{ color: themeColor }} />
    {role?.role_name || '—'}
  </span>
);


const Avatar = ({ photo, name }) => (
  <div
    className="rounded-circle d-flex align-items-center justify-content-center mr-3"
    style={{
      width: 44,
      height: 44,
      background: '#EEF2F6',
      color: '#0F172A',
      fontWeight: 700,
      overflow: 'hidden',
      flex: '0 0 44px',
    }}
  >
    {photo ? (
      <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    ) : (
      initialsOf(name)
    )}
  </div>
);

const UserCard = ({ user, selected, onSelect }) => {
  const fullName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();

  return (
    <div
      onClick={() => onSelect(user)}
      style={{
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: `1px solid ${selected ? themeColor : '#F1F5F9'}`,
        borderRadius: '16px',
        backgroundColor: '#FFFFFF',
        padding: '20px',
        position: 'relative',
        height: '100%',
        boxShadow: selected ? '0 10px 20px -5px rgba(79, 70, 229, 0.1)' : '0 1px 3px rgba(0,0,0,0.02)'
      }}
      className="system-user-card"
    >
      <div className="d-flex align-items-start">
        <div style={{ position: 'relative' }}>
            <div className="rounded-circle d-flex align-items-center justify-content-center"
                 style={{ width: 52, height: 52, background: '#F1F5F9', fontWeight: 700, overflow: 'hidden' }}>
                {user.photo ? <img src={user.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : fullName.charAt(0)}
            </div>
            {selected && (
                <div style={{ position: 'absolute', bottom: -2, right: -2, backgroundColor: '#FFF', borderRadius: '50%', padding: '2px' }}>
                    <i className="fa fa-check-circle" style={{ color: themeColor, fontSize: '16px' }} />
                </div>
            )}
        </div>

        <div className="ml-3 flex-grow-1">
          <div style={{ fontWeight: '700', fontSize: '15px', color: '#1E293B', marginBottom: '2px' }}>{fullName}</div>
          <div style={{ color: '#64748B', fontSize: '13px' }}>{user.username}</div>
        </div>
      </div>

      <div className="mt-4 d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
        <RolePill role={user.role} />
        <StatusBadge status={user.status} />
      </div>

      <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center" style={{ color: '#94A3B8', fontSize: '12px' }}>
        <span><i className="fa fa-phone mr-1" /> {user.phone_number || '—'}</span>
        <span><i className="fa fa-calendar-alt mr-1" /> {functions.formatDate(user.updated_at)}</span>
      </div>
    </div>
  );
};
/* ---------- Toolbar (search + status/role filters) ---------- */
const Toolbar = ({ query, setQuery, status, setStatus, role, setRole, roles = [] }) => (
  <div className="d-flex flex-column flex-md-row align-items-md-center px-4 py-3 border-bottom mb-4" style={{ gap: '12px', backgroundColor: '#FBFCFE' }}>
    <div className="input-group flex-nowrap" style={{ maxWidth: 320 }}>
      <div className="input-group-prepend" style={{ backgroundColor: '#F1F5F9', border: '1px solid #E2E8F0', borderRight: 'none', borderRadius: '10px 0 0 10px', padding: '0 12px', display: 'flex', alignItems: 'center' }}>
        <i className="fa fa-search text-muted" />
      </div>
      <input
        type="text"
        className="form-control"
        placeholder="Search users..."
        style={{ borderRadius: '0 10px 10px 0', border: '1px solid #E2E8F0', height: '42px', fontSize: '14px', boxShadow: 'none' }}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </div>

    <div className="d-flex align-items-center ml-md-auto" style={{ gap: '10px' }}>
        <select className="custom-select" style={{ borderRadius: '10px', height: '42px', border: '1px solid #E2E8F0', width: '140px', fontSize: '14px' }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>

        <select className="custom-select" style={{ borderRadius: '10px', height: '42px', border: '1px solid #E2E8F0', width: '160px', fontSize: '14px' }} value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All Roles</option>
          {roles.map((r) => <option value={r.role_name} key={r.role_name}>{r.role_name}</option>)}
        </select>
    </div>
  </div>
);

/* ---------- Skeleton grid while loading ---------- */
const UsersSkeleton = () => (
  <div className="row">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="col-lg-4 col-md-6 mb-3" key={i}>
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <SkeletonCircle size={44} />
              <div className="ml-3" style={{ width: '70%' }}>
                <Skeleton w="90%" h={14} />
                <Skeleton w="60%" h={12} style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="mt-3">
              <Skeleton w="40%" h={22} />
              <Skeleton w="30%" h={22} style={{ marginLeft: 8 }} />
            </div>
            <div className="mt-3">
              <Skeleton w="100%" h={10} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ---------- Main component ---------- */
const SystemUsers = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [roleFilter, setRole] = useState('');

  const { getUsers, userListing, dispatch, loadingUsers, errorUsers, userRoles, setModifiedUser } = useConfig();

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNew = () => openSettings('Invite New User', 'ConfigNewUser', null, "Invite & Add");
  const handleDelete = () => activeRecord && dispatch({ type: 'deletedUser', payload: activeRecord });
  const handleChangeRole = () => activeRecord && setModifiedUser({ show: true, user: activeRecord, callback: () => getUsers(true) });

  const rolesForFilter = useMemo(() => {
    // if provided by store; otherwise derive from listing
    if (Array.isArray(userRoles) && userRoles.length) return userRoles;
    const names = Array.from(new Set((userListing || []).map((u) => u.role?.role_name).filter(Boolean)));
    return names.map((n, i) => ({ id: i + '', role_name: n }));
  }, [userRoles, userListing]);

  const filtered = useMemo(() => {
    const list = Array.isArray(userListing) ? userListing : [];
    const q = query.trim().toLowerCase();
    return list
      .filter((u) => (q ? (u.full_name || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q) : true))
      .filter((u) => (status ? String(u.status).toLowerCase() === status.toLowerCase() : true))
      .filter((u) => (roleFilter ? String(u.role?.role_name || '').toLowerCase() === roleFilter.toLowerCase() : true))
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''));
  }, [userListing, query, status, roleFilter]);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleNew}>
        <i className="dropdown-icon fa-regular fa-paper-plane"></i>
        <span>Invite User</span>
      </button>
       <button type="button" tabIndex="0" className="dropdown-item" disabled={!activeRecord} onClick={handleChangeRole}>
        <i className="dropdown-icon fa fa-user-shield text-success" /> <span>Change Role</span>
      </button>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={() => getUsers(true)}>
        <i className="dropdown-icon fa fa-sync"></i>
        <span>Refresh</span>
      </button>
      <div className="dropdown-divider"></div>
      <button type="button" tabIndex="0" className="dropdown-item text-danger" disabled={!activeRecord} onClick={handleDelete}>
        <i className="dropdown-icon fa fa-trash-alt" /> <span>Remove User</span>
      </button>
    </>
  );

  return (
    <SettingContainer title="System Users" icon="fa fa-user" Menu={menu}>
      {/* Toolbar */}
      <Toolbar
        query={query}
        setQuery={setQuery}
        status={status}
        setStatus={setStatus}
        role={roleFilter}
        setRole={setRole}
        roles={rolesForFilter}
      />

      {/* States */}
      {errorUsers && <Notification type="danger" message={String(errorUsers)} />}

      {(!userListing && !errorUsers) || loadingUsers ? (
        <UsersSkeleton />
      ) : Array.isArray(userListing) && userListing.length === 0 ? (
        <div className="text-center p-5 border rounded">
          <div className="mb-2">
            <span className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light" style={{ width: 64, height: 64 }}>
              <i className="fa fa-users text-muted" />
            </span>
          </div>
          <div className="h6">No users yet</div>
          <div className="text-muted mb-3">Invite teammates to collaborate on your account.</div>
          <button className="btn btn-primary btn-sm" onClick={handleNew}>
            <i className="fa fa-paper-plane mr-1" />
            Invite User
          </button>
        </div>
      ) : (
        <div className="row">
          {filtered.map((u) => (
            <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={u.user_id}>
              <UserCard user={u} selected={activeRecord?.user_id === u.user_id} onSelect={setActiveRecord} />
            </div>
          ))}
        </div>
      )}
    </SettingContainer>
  );
};

export default SystemUsers;
